import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  Box, Container, Typography, Button, Avatar, CircularProgress, Alert,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Download as DownloadIcon,
  ChildCare as ChildCareIcon,
} from '@mui/icons-material';
import { reportsAPI } from '../../services/api';
import { nameInitial, downloadBlob, relabelMonthExport, FORM_TYPE_TABS, MotherVisitTable } from '../components/reportUi';

// Full-page version of what used to be SupervisorReports.js's
// MotherDetailDialog modal — same data/export, just given its own route and
// room to breathe instead of being squeezed into a dialog. Reached by
// clicking a row on the आशा भेटी अहवाल / supervisor / taluka-admin reports
// list (see openDetail in SupervisorReports.js, which builds the ?formType=
// &month=... query string this page reads back).
function WorkerVisitDetail() {
  const { ashaWorkerId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const ashaWorkerName = searchParams.get('name') || '';
  const formType = searchParams.get('formType') || 'all';
  const isAll = formType === 'all';

  // Same "back to the list" path for every one of the three report route
  // prefixes (admin/reports/asha-visits, admin/supervisor/reports,
  // admin/taluka-admin/reports) — just strip this page's own :ashaWorkerId
  // segment off the current URL rather than hardcoding any one prefix.
  const backUrl = location.pathname.replace(/\/[^/]+$/, '');

  const baseExportParams = (() => {
    const params = { form_type: formType };
    const month = searchParams.get('month');
    const dateFrom = searchParams.get('dateFrom');
    const dateTo = searchParams.get('dateTo');
    if (month) params.month = month;
    else {
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
    }
    return params;
  })();
  const isMonthFiltered = !!baseExportParams.month;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [mothers, setMothers] = useState([]);
  const [visitDays, setVisitDays] = useState([]);
  const [byType, setByType] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError('');
    reportsAPI.getAshaWorkerMotherDetail(ashaWorkerId, baseExportParams)
      .then((res) => {
        if (formType === 'all') {
          setByType(res.data.byType);
        } else {
          setMothers(res.data.mothers);
          setVisitDays(res.data.visitDays);
        }
      })
      .catch(() => setError('तपशील लोड करताना त्रुटी आली'))
      .finally(() => setLoading(false));
    // Filter query params are fixed for the lifetime of this page (set once
    // by the link that brought us here) — only ashaWorkerId/formType matter
    // as dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ashaWorkerId, formType]);

  const totalMothers = isAll
    ? Object.values(byType || {}).reduce((sum, t) => sum + t.mothers.length, 0)
    : mothers.length;
  const totalVisits = isAll
    ? Object.values(byType || {}).reduce((sum, t) => sum + t.mothers.reduce((s, m) => s + m.totalVisits, 0), 0)
    : mothers.reduce((sum, m) => sum + m.totalVisits, 0);

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const res = await reportsAPI.exportAshaWorkerMotherDetail(ashaWorkerId, { ...baseExportParams, format });
      const file = isMonthFiltered ? await relabelMonthExport(res.data, format) : res.data;
      downloadBlob(file, `${(ashaWorkerName || 'asha').replace(/\s+/g, '_')}_${formType}_bheti_tapshil.${format}`);
    } catch {
      setError('एक्सपोर्ट करताना त्रुटी आली');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box>
      {/* Hero header — full-width banner instead of the old dialog's small
          gradient strip, now with room for a proper back link and bigger stats. */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1976d2 0%, #1259a6 100%)',
          color: '#fff',
          px: { xs: 2, sm: 4 },
          pt: { xs: 2, sm: 3 },
          pb: { xs: 3, sm: 4 },
          borderRadius: { xs: 0, sm: '0 0 28px 28px' },
          mb: 3,
        }}
      >
        <Container maxWidth="xl" disableGutters>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(backUrl)}
            sx={{
              color: 'rgba(255,255,255,0.9)', textTransform: 'none', fontWeight: 600, mb: 2,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' },
            }}
          >
            अहवालाकडे परत
          </Button>
          <Box
            sx={{
              display: 'flex', flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2.5,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
              <Avatar
                sx={{
                  bgcolor: 'rgba(255,255,255,0.18)', color: '#fff', width: 60, height: 60,
                  fontSize: '1.6rem', fontWeight: 700, border: '2px solid rgba(255,255,255,0.35)', flexShrink: 0,
                }}
              >
                {nameInitial(ashaWorkerName)}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.25rem', sm: '1.6rem' }, lineHeight: 1.25 }}>
                  {ashaWorkerName}
                </Typography>
                <Typography sx={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.85)', mt: 0.25 }}>
                  माता व भेटी तपशील · {isAll ? 'सर्व फॉर्म' : formType.toUpperCase()}
                </Typography>
              </Box>
            </Box>
            {!loading && totalMothers > 0 && (
              <Box sx={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                <Box>
                  <Typography sx={{ fontSize: '1.8rem', fontWeight: 800, lineHeight: 1 }}>{totalMothers}</Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.82)', mt: 0.5 }}>माता</Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '1.8rem', fontWeight: 800, lineHeight: 1 }}>{totalVisits}</Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.82)', mt: 0.5 }}>{isMonthFiltered ? 'या महिन्यातील भेटी' : 'एकूण भेटी'}</Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ pb: 4 }}>
        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {!loading && totalMothers > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.25, mb: 2.5, flexWrap: 'wrap' }}>
            <Button
              startIcon={<DownloadIcon />} variant="outlined" size="small" disabled={exporting}
              onClick={() => handleExport('csv')}
              sx={{ textTransform: 'none', borderRadius: 1.5, fontWeight: 600 }}
            >
              CSV
            </Button>
            <Button
              startIcon={exporting ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon />}
              variant="contained" size="small" disabled={exporting}
              onClick={() => handleExport('xlsx')}
              sx={{ textTransform: 'none', borderRadius: 1.5, fontWeight: 600, boxShadow: 'none' }}
            >
              Excel एक्सपोर्ट करा
            </Button>
          </Box>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
        ) : totalMothers === 0 ? (
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <ChildCareIcon sx={{ fontSize: 48, color: 'grey.300', mb: 1.5 }} />
            <Typography color="text.secondary">निवडलेल्या कालावधीत या आशा कार्यकर्तीने कोणतीही भेट पूर्ण केलेली नाही</Typography>
          </Box>
        ) : isAll ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.5 }}>
            {FORM_TYPE_TABS.filter((t) => t.key !== 'all').map((t) => {
              const section = byType?.[t.key];
              if (!section || section.mothers.length === 0) return null;
              return (
                <Box key={t.key}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', mb: 1.25, color: 'grey.800' }}>{t.label}</Typography>
                  <MotherVisitTable mothers={section.mothers} visitDays={section.visitDays} formType={t.key} isMonthFiltered={isMonthFiltered} />
                </Box>
              );
            })}
          </Box>
        ) : (
          <MotherVisitTable mothers={mothers} visitDays={visitDays} formType={formType} isMonthFiltered={isMonthFiltered} />
        )}
      </Container>
    </Box>
  );
}

export default WorkerVisitDetail;
